<script>
import Ripple from 'vue-ripple-directive'
import chroma from 'chroma-js'

Ripple.zIndex = 1

export default {
  name: 'Button',
  directives: {
    ripple: Ripple
  },
  props: {
    size: {
      type: String,
      default: 'lg'
    },
    outlined: {
      type: Boolean,
      default: false
    },
    flat: {
      type: Boolean,
      default: false
    },
    text: {
      type: String,
      default: ''
    },
    textColor: {
      type: String,
      default: 'white'
    },
    color: {
      type: String,
      default: '#1F74D9'
    },
    scale: {
      type: Number,
      default: 1
    }
  },
  computed: {
    rippleColor () {
      return (this.outlined || this.flat) ? chroma(this.color).alpha(0.3).css() : chroma(this.color).brighten().css()
    },
    styles () {
      const scale = {
        md: this.scale * 1,
        lg: this.scale * 1.5
      }[this.size]
      return {
        button: {
          '--height': `${36 * scale}px`
        },
        label: {
          '--text-transform': !this.flat ? 'uppercase' : 'none',
          '--text-color': (this.outlined || this.flat) ? this.color : this.textColor,
          '--font-size': `${14 * scale}px`,
          '--horizonal-padding': `${16 * scale}px`
        },
        surface: {
          '--hover-focus-shadow': !(this.outlined || this.flat) && '0px 2px 4px -1px rgba(0, 0, 0, 0.2), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgba(0,0,0,.12)',
          '--active-shadow': !(this.outlined || this.flat) && '0px 5px 5px -3px rgba(0, 0, 0, 0.2), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 3px 14px 2px rgba(0,0,0,.12)',
          '--normal-shadow': !(this.outlined || this.flat) && '0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0,0,0,.12)',
          '--border-color': this.outlined ? this.color : 'transparent',
          '--normal-bg-color': (this.outlined || this.flat) ? 'transparent' : this.color,
          '--hover-bg-color': (this.outlined || this.flat) ? chroma(this.color).alpha(0.1).css() : chroma(this.color).brighten(0.25).css(),
          '--active-bg-color': (this.outlined || this.flat) ? chroma(this.color).alpha(0.2).css() : chroma(this.color).brighten(0.50).css()
        }
      }
    }
  }
}
</script>
<template>
  <button
    :style="styles.button"
    @click="e => $emit('click', e)"
  >
    <div
      class="surface"
      :style="styles.surface"
    >
      <div
        v-ripple="rippleColor"
        class="ripple"
      />
    </div>
    <span
      class="label"
      :style="styles.label"
    >{{ text }}</span>
  </button>
</template>

<style lang="scss" scoped>
button {
  text-transform: none;
  -webkit-appearance: button;
  overflow: visible;
  font-family: inherit;
  font-size: 100%;
  line-height: 1.15;
  margin: 0;
  padding: 0;
  height: var(--height);
  border: none;
  position: relative;
  background-color: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 0;
  cursor: pointer;
  &:hover, &:focus {
    > .surface {
      box-shadow: var(--hover-focus-shadow);
      background-color: var(--hover-bg-color);
    }
  }
  &:active {
    > .surface {
      box-shadow: var(--active-shadow);
      background-color: var(--active-bg-color);
    }
  }
  &:focus {
    outline: 0;
  }
  > .surface {
    height: 100%;
    width: 100%;
    background-color: var(--normal-bg-color);
    position: absolute;
    border-radius: calc(var(--height) / 2);
    box-shadow: var(--normal-shadow);
    transition: background-color 280ms cubic-bezier(0.4, 0, 0.2, 1),
                box-shadow 280ms cubic-bezier(0.4, 0, 0.2, 1);
    overflow: hidden;
    border: 1px solid var(--border-color);
    box-sizing: border-box;
    > .ripple {
      height: 100%;
      width: 100%;
    }
  }
  > .label {
    color: var(--text-color);
    padding: 0 var(--horizonal-padding);
    z-index: 2;
    pointer-events: none;
    font-weight: 600;
    font-family: 'Open Sans', sans-serif;
    font-size: var(--font-size);
    text-transform: var(--text-transform);
  }
}
</style>
